import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from './client.ts';
import { useSession } from '../auth/session.tsx';
import { queryDefaults } from './queries.ts';

/**
 * A commission and an oath and designation recorded against a court order (154). The record is the act; its date is
 * the ORDER's (read here as `date`, never stored on the record). The server decides who may record
 * (may_record_commission()) and names and files any document (Legal > Court Documents).
 */

export type OrderRecordKind = 'commission' | 'oath_designation';

export interface OrderRecord {
  id: string;
  kind: OrderRecordKind;
  withdrawn: boolean;
  current_version: number;
  created_at: string;
  /** The order's date for this kind: the commission issued, or the oath and designation filed. */
  date: string | null;
  upload_id: string | null;
  upload_name: string | null;
  upload_status: string | null;
  /** A linked Clio copy; its name is shown in Documents, never here (the API cannot read it). */
  clio_copy_id: string | null;
  recorded_by: string | null;
  edits: number;
}

export interface CommissionOrder {
  id: string;
  label: string | null;
  signed_date: string | null;
  is_temporary: boolean;
  /** "Person and Property", "Person" or "Property", from the order. */
  powers: string | null;
  carries_commission: boolean | null;
  commissioned_date: string | null;
  oath_filed_date: string | null;
  records: OrderRecord[];
}

export const KIND_WORDS: Record<OrderRecordKind, string> = {
  commission: 'Commission',
  oath_designation: 'Oath and designation',
};

function post<T>(getToken: () => Promise<string>, path: string, body: unknown) {
  return apiFetch<T>(path, getToken, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  });
}

export function useCommissionOrders(clientId: string, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'commission-orders'],
    queryFn: () => apiFetch<{ orders: CommissionOrder[]; may_record: boolean }>(`/clients/${clientId}/commission-orders`, getToken),
    ...queryDefaults,
    enabled,
  });
}

/** Everything a record changes: the order's dates, its deadlines and tasks, the tracker, the client's documents. */
function useRecordChanged(clientId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
    void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    void queryClient.invalidateQueries({ queryKey: ['commissions'] });
  };
}

export function useCreateOrderRecord(clientId: string) {
  const { getToken } = useSession();
  const changed = useRecordChanged(clientId);
  return useMutation({
    mutationFn: ({ orderId, ...body }: { orderId: string; kind: OrderRecordKind; date: string;
                                         upload_id?: string | null; clio_copy_id?: string | null }) =>
      post<{ id: string }>(getToken, `/court_orders/${orderId}/records`, body),
    onSuccess: changed,
  });
}

export function useCorrectOrderRecord(clientId: string) {
  const { getToken } = useSession();
  const changed = useRecordChanged(clientId);
  return useMutation({
    mutationFn: ({ recordId, ...body }: { recordId: string; date: string; upload_id: string | null;
                                          clio_copy_id: string | null; reason: string; reason_text?: string }) =>
      post<{ version: number }>(getToken, `/order_records/${recordId}/correct`, body),
    onSuccess: changed,
  });
}

export function useWithdrawOrderRecord(clientId: string) {
  const { getToken } = useSession();
  const changed = useRecordChanged(clientId);
  return useMutation({
    mutationFn: ({ recordId, reason }: { recordId: string; reason: string }) =>
      post<{ version: number }>(getToken, `/order_records/${recordId}/withdraw`, { reason }),
    onSuccess: changed,
  });
}

/** The document, uploaded through the record: the server names it and files it; the file's own name gives only its type. */
export function useAttachOrderRecordDocument(clientId: string) {
  const { getToken } = useSession();
  const changed = useRecordChanged(clientId);
  return useMutation({
    mutationFn: async ({ recordId, file }: { recordId: string; file: File }) => {
      const res = await fetch(`/api/order_records/${recordId}/document`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${await getToken()}`,
          'content-type': 'application/octet-stream',
          'x-file-name': encodeURIComponent(file.name),
          ...(file.type ? { 'x-file-type': file.type } : {}),
        },
        body: file,
      });
      if (!res.ok) {
        let message = `The document could not be attached (${res.status}).`;
        let hint: string | undefined;
        try { const b = (await res.json()) as { error?: string; hint?: string }; message = b.error ?? message; hint = b.hint; } catch { /* not JSON */ }
        throw new ApiError(res.status, message, hint);
      }
      return (await res.json()) as { upload_id: string; name: string; version: number };
    },
    onSuccess: changed,
  });
}
